/**
 * LanguageSelectorContent
 *
 * Body of the `LanguageSelectorSheet`: an always-visible, virtualized list of
 * every offered language — one `ListItemTile` row each, endonym as the primary
 * label and English name as the secondary line — with the app's own check-mark
 * asset marking the active locale. Tapping a row calls `setLocale`, which is
 * the entire selection side-effect (`LocaleContext` persists it to
 * localStorage `locale` and re-renders the tree); the sheet deliberately stays
 * open so the user sees the sheet title re-render in the chosen language.
 *
 * Replaces the sheet's former collapsed language dropdown (deleted on this
 * branch), whose absolutely positioned panel overflowed the sheet's scroll box
 * so only one language was visible at a time. Ordering is owned by
 * `useLanguageOptions` (alphabetical by endonym); no search box is offered —
 * the list is short.
 *
 * **Web-only surface — there is no Flutter counterpart to keep parity with.**
 * A repo-wide grep of `/lib` for `Language`, `setLocale` and `supportedLocales`
 * returns zero hits, and `lib/main.dart` carries no `locale` /
 * `localizationsDelegates` wiring: the Flutter app ships a single hardcoded
 * language and has no picker, exactly as this file's twin
 * `ThemeSelector.tsx:13-16` records for theming. Every decision below is a web
 * UI decision governed by `presentation.md`.
 *
 * Deliberate choices — do not "restore" the old look:
 *  - the body paints NO background of its own, so the sheet/dialog shell's
 *    `theme.modalBackground` shows through, matching every other sheet body;
 *  - the "Language" heading is NOT rendered here: the sheet already renders it
 *    as its own title row (`LanguageSelectorSheet`, `l10n.settings.language`),
 *    so an in-content heading would show the same copy twice;
 *  - the selected row carries no background fill — the check mark is the
 *    selection indicator, and `ListItemTile`'s `hover:opacity-80` is the hover
 *    affordance the old hand-rolled mouse handlers provided;
 *  - the check tint is `theme.exclusive` (the app's amber highlight, the tint
 *    the outgoing `Check` glyph already used). NOT `theme.icon.selected`
 *    (`#ff0000` in the light palette — a red check misreads as an error) and
 *    NOT `theme.white` (invisible on a light-theme modal);
 *  - this body owns its own scroll container (root `height: 100%` + an inner
 *    `flex: 1; overflowY: auto` div passed to `VirtualList` as
 *    `scrollContainer`), the proven in-sheet list shape from
 *    `RoadmapItemLikesContent.tsx:104-181`. That pairing only works when the
 *    sheet config sets `fixedHeight` **and** `scrollable: false`
 *    (`RoadmapItemLikesSheet.tsx:51-52`): left scrollable, the shell keeps its
 *    own scroller plus a non-passive `wheel` interceptor over this nested
 *    scroller and turns a downward swipe into a sheet drag;
 *  - the list opens scrolled to the language that was active when the sheet
 *    opened, so a user whose language sorts late still sees the check mark
 *    without scrolling. Four facts hold that up, none of which should be
 *    "simplified" away:
 *      • the opening index is owned by `useInitialLanguageIndex` (a sibling of
 *        `useLanguageOptions`), NOT derived here — it is a function of the
 *        option ordering that hook owns, and this component must not
 *        re-implement that ordering;
 *      • the mechanism is `VirtualList`'s `initialIndex` prop, NOT the
 *        imperative `listRef.scrollToIndex` of
 *        `ContentCommentsContent.tsx:71-79`. `VirtualList`'s first commit
 *        renders zero rows (`visibleRange` starts `{start: 0, end: 0}`), so a
 *        mount-effect `scrollToIndex` finds no `[data-index]` element and
 *        no-ops; that pattern only survives there because comments arrive
 *        asynchronously and the effect retries on a later commit. Here
 *        `options` (a `useMemo` over a module constant) and `locale` are final
 *        on render 1, so there is no later commit to retry on. `initialIndex`
 *        force-renders rows `0..index` for measurement and retries the scroll
 *        itself;
 *      • the index is frozen at open, so picking a language in the
 *        (deliberately still-open) sheet does not yank the list back to the
 *        row just tapped;
 *      • this supersedes the `task_4_plan.md` "do not pass `initialIndex`"
 *        bullet: the measurement phase it objected to is gated to `index > 0`
 *        (so it never runs when `English` is active or the locale is
 *        unresolvable) and, when it does run, its ~100ms first attempt sits
 *        entirely inside the sheet's 300ms open animation.
 */

import React, { useCallback, useRef } from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import { ListItemTile } from '@/presentation/components/common/ListItemTile';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { LocaleOption } from '@/presentation/locale';
import { useLanguageOptions } from '../hooks/useLanguageOptions';
import { useInitialLanguageIndex } from '../hooks/useInitialLanguageIndex';

export const LanguageSelectorContent: React.FC = () => {
  const { locale, setLocale } = useLocale();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const options = useLanguageOptions();
  const initialSelectedIndex = useInitialLanguageIndex(options);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const renderItem = useCallback(
    (option: LocaleOption) => {
      const isSelected = locale === option.code;

      return (
        <ListItemTile
          title={option.nativeName}
          description={option.name}
          onClick={() => setLocale(option.code)}
          trailing={
            isSelected ? (
              <AssetIcon
                path="/assets/images/check.png"
                width={responsive.icon.small}
                height={responsive.icon.small}
                color={theme.exclusive}
              />
            ) : undefined
          }
          qa={{
            id: `language-option-${option.code}`,
            ...(isSelected ? { value: 'selected' } : {}),
          }}
        />
      );
    },
    [locale, setLocale, responsive.icon.small, theme.exclusive],
  );

  return (
    <div
      {...qaAttr({ id: 'language-selector-content' })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div
        ref={scrollRef}
        className="scrollbar-hide"
        style={{
          flex: 1,
          overflowY: 'auto',
          // Makes THIS element the `offsetParent` of everything `VirtualList`
          // renders, so the `offsetTop`-based scroll math behind `initialIndex`
          // (`VirtualList.tsx:1138-1148`) is expressed in this scroller's own
          // coordinate system. Left static, the nearest positioned ancestor is
          // the `fixed` sheet root (`BottomSheetView.tsx:350`) and the landing
          // offset over-shoots by the sheet's drag-bar + title chrome.
          // Visually inert: `position: relative` with `z-index: auto` creates no
          // stacking context and applies no offset.
          position: 'relative',
          paddingTop: responsive.spacing.base,
          paddingBottom: responsive.spacing.base,
          paddingLeft: responsive.spacing.lg,
          paddingRight: responsive.spacing.lg,
        }}
      >
        <VirtualList
          items={options}
          renderItem={renderItem}
          getItemKey={(option) => option.code}
          scrollContainer={scrollRef}
          initialIndex={initialSelectedIndex}
          gap={responsive.gap.sm}
          defaultItemHeight={responsive.list.defaultItemHeight}
          qa={{ id: 'language-selector-list' }}
        />
      </div>
    </div>
  );
};
